import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { Alert, Button, Empty, List, Modal, Pagination, Result, Space, Tabs, Tag, Typography,message } from "antd";
import dayjs from "dayjs";
import { LIST_TASKS, UPDATE_TASK } from "../graphql/queries";
import { useAppTheme } from "../theme/ThemeProvider";

const { Text, Paragraph } = Typography;

/** Při /tasks/:id načteme větší stránku stejného seznamu (bez filtru podle ID), abychom úkol našli v odpovědi. */
const TASK_DEEP_LINK_FETCH_LIMIT = 2000;

type TaskTab = "active" | "initiated";

interface TaskDetail {
  task_id?: string;
  task_title?: string;
  task_description?: string;
  task_priority?: string;
  task_status?: string;
  task_start_time?: string;
  task_end_time?: string;
  task_created_at?: string;
  task_updated_at?: string;
  initiator_id?: string;
  initiator_name?: string;
  initiator_login?: string;
  assignee_id?: string;
  assignee_name?: string;
  assignee_login?: string;
  event_name?: string;
  inventory_name?: string;
  question_text?: string;
  place_name?: string;
  check_note?: string;
  val_boolean?: boolean | null;
  val_text?: string | null;
  val_number?: number | null;
  val_real?: number | null;
  val_lov?: string | null;
  val_file?: string | null;
  value_text?: string | null;
}

interface TaskDetailListResponse {
  total: number;
  items: TaskDetail[];
}

function getTaskStatusTagColor(status?: string): string {
  const s = (status || "").toLowerCase();
  if (s === "completed") return "success";
  if (s === "in_progress" || s === "open") return "processing";
  if (s === "cancelled" || s === "canceled") return "error";
  return "default";
}

function getTaskStatusLabel(status?: string): string {
  const s = (status || "").toLowerCase();
  if (s === "completed") return "Vyřešeno";
  if (s === "in_progress") return "Rozpracováno";
  if (s === "open") return "Otevřeno";
  if (s === "cancelled" || s === "canceled") return "Zrušeno";
  return status || "—";
}

function getTaskAnswerDisplayText(item: TaskDetail): string {
  if (item.val_lov != null && item.val_lov !== "") return String(item.val_lov);
  if (item.val_text != null && item.val_text !== "") return String(item.val_text);
  if (item.val_number != null) return String(item.val_number);
  if (item.val_real != null) return String(item.val_real);
  if (item.val_boolean != null) return item.val_boolean ? "Ano" : "Ne";
  if (item.val_file) return "Soubor přiložen";
  if (item.value_text) return item.value_text;
  return "—";
}

export default function TasksPage() {
  const { themeName } = useAppTheme();
  const navigate = useNavigate();
  const { taskId: taskIdFromRoute } = useParams<{ taskId?: string }>();
  const userId = localStorage.getItem("userId") || "";
  const [activeTab, setActiveTab] = useState<TaskTab>("active");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedTask, setSelectedTask] = useState<TaskDetail | null>(null);
  const [updatingTask, setUpdatingTask] = useState(false);
  const lastDeepLinkResolvedId = useRef<string | null>(null);

  const hasTaskIdInUrl = Boolean(taskIdFromRoute);

  const { filtersActive, filtersInitiated, filtersForCurrentTab } = useMemo(() => {
    const common = [
      { columnName: "e.task_status", operator: "equal", value: "in_progress" },
    ];
    return {
      filtersActive: [...common, { columnName: "e.assignee_id", operator: "equal", value: userId }],
      filtersInitiated: [...common, { columnName: "e.initiator_id", operator: "equal", value: userId }],
      filtersForCurrentTab:
        activeTab === "active"
          ? [...common, { columnName: "e.assignee_id", operator: "equal", value: userId }]
          : [...common, { columnName: "e.initiator_id", operator: "equal", value: userId }],
    };
  }, [activeTab, userId]);

  const listVars = {
    sort: { columnName: "task_created_at" as const, direction: "DESC" as const },
    search: null as string | null,
  };

  const { data: dataList, loading: loadingList, error: errorList, refetch: refetchList } = useQuery<{
    listTasks: TaskDetailListResponse;
  }>(LIST_TASKS, {
    variables: {
      filters: filtersForCurrentTab,
      ...listVars,
      pagination: { limit: pageSize, offset: (currentPage - 1) * pageSize },
    },
    skip: !userId || hasTaskIdInUrl,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const { data: dataDeepActive, loading: loadingDeepA, error: errorDeepA, refetch: refetchDeepA } = useQuery<{
    listTasks: TaskDetailListResponse;
  }>(LIST_TASKS, {
    variables: {
      filters: filtersActive,
      ...listVars,
      pagination: { limit: TASK_DEEP_LINK_FETCH_LIMIT, offset: 0 },
    },
    skip: !userId || !hasTaskIdInUrl,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const { data: dataDeepInitiated, loading: loadingDeepI, error: errorDeepI, refetch: refetchDeepI } = useQuery<{
    listTasks: TaskDetailListResponse;
  }>(LIST_TASKS, {
    variables: {
      filters: filtersInitiated,
      ...listVars,
      pagination: { limit: TASK_DEEP_LINK_FETCH_LIMIT, offset: 0 },
    },
    skip: !userId || !hasTaskIdInUrl,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const [updateTaskMutation] = useMutation(UPDATE_TASK);

  const refetchTaskLists = useCallback(async () => {
    if (hasTaskIdInUrl) {
      await Promise.all([refetchDeepA(), refetchDeepI()]);
    } else {
      await refetchList();
    }
  }, [hasTaskIdInUrl, refetchDeepA, refetchDeepI, refetchList]);

  const itemsActiveDeep = dataDeepActive?.listTasks?.items ?? [];
  const itemsInitiatedDeep = dataDeepInitiated?.listTasks?.items ?? [];

  const items = hasTaskIdInUrl
    ? activeTab === "active"
      ? itemsActiveDeep
      : itemsInitiatedDeep
    : dataList?.listTasks?.items ?? [];

  const total = hasTaskIdInUrl
    ? (activeTab === "active" ? dataDeepActive?.listTasks?.total : dataDeepInitiated?.listTasks?.total) ?? 0
    : dataList?.listTasks?.total ?? 0;

  const loading = hasTaskIdInUrl ? loadingDeepA || loadingDeepI : loadingList;
  const error = hasTaskIdInUrl ? errorDeepA || errorDeepI : errorList;

  const foundInActiveDeep = taskIdFromRoute
    ? itemsActiveDeep.find((t) => t.task_id === taskIdFromRoute)
    : undefined;
  const foundInInitiatedDeep = taskIdFromRoute
    ? itemsInitiatedDeep.find((t) => t.task_id === taskIdFromRoute)
    : undefined;

  useEffect(() => {
    if (!hasTaskIdInUrl) {
      lastDeepLinkResolvedId.current = null;
      return;
    }
    if (!taskIdFromRoute) {
      return;
    }
    if (loadingDeepA || loadingDeepI) {
      return;
    }

    const inA = foundInActiveDeep;
    const inI = foundInInitiatedDeep;

    if (lastDeepLinkResolvedId.current !== taskIdFromRoute) {
      lastDeepLinkResolvedId.current = taskIdFromRoute;
      if (inA && !inI) {
        setActiveTab("active");
        setSelectedTask(inA);
      } else if (inI && !inA) {
        setActiveTab("initiated");
        setSelectedTask(inI);
      } else if (inA && inI) {
        setActiveTab("active");
        setSelectedTask(inA);
      } else {
        setSelectedTask(null);
      }
    } else {
      if (inA || inI) {
        setSelectedTask(inA || inI || null);
      }
    }
  }, [
    hasTaskIdInUrl,
    taskIdFromRoute,
    loadingDeepA,
    loadingDeepI,
    foundInActiveDeep,
    foundInInitiatedDeep,
  ]);

  if (!userId) {
    return (
      <div style={{ padding: 16 }}>
        <Result
          status="403"
          title="Tato stránka je dostupná jen pro přihlášené uživatele."
        />
      </div>
    );
  }

  const closeTaskModal = () => {
    setSelectedTask(null);
    if (taskIdFromRoute) {
      navigate("/tasks", { replace: true });
    }
  };

  const taskFromListByUrl = foundInActiveDeep || foundInInitiatedDeep;
  const deepLinkTaskMissing =
    hasTaskIdInUrl &&
    Boolean(taskIdFromRoute) &&
    !loading &&
    !foundInActiveDeep &&
    !foundInInitiatedDeep;

  return (
    <div style={{ padding: 12, height: "calc(100vh - 72px)", display: "flex", flexDirection: "column" }}>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key as TaskTab);
          setCurrentPage(1);
        }}
        items={[
          { key: "active", label: "Moje aktivní úkoly" },
          { key: "initiated", label: "Moje iniciované úkoly" },
        ]}
      />

      {error ? (
        <Alert type="error" message="Nepodařilo se načíst úkoly." style={{ marginBottom: 10 }} />
      ) : null}

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          overflowX: "hidden",
        }}
      >
        <List
          bordered={false}
          loading={loading}
          dataSource={items}
          locale={{
            emptyText: (
              <Empty
                description={
                  hasTaskIdInUrl && !loading && !taskFromListByUrl
                    ? "Tento úkol v načtených seznamech není, nebo seznamy jsou prázdné."
                    : "Žádné úkoly"
                }
              />
            ),
          }}
          renderItem={(item) => {
            const selected = selectedTask?.task_id === item.task_id;
            const baseClass =
              themeName === "dark" ? "places-list-item--dark" : "places-list-item--light";
            const selectedClass =
              selected && (themeName === "dark" ? "places-list-item--selected--dark" : "places-list-item--selected--light");
            return (
              <List.Item
                className={["places-list-item", baseClass, selectedClass].filter(Boolean).join(" ")}
                style={{
                  cursor: "pointer",
                  alignItems: "flex-start",
                }}
                onClick={() => {
                  setSelectedTask(item);
                  if (item.task_id) {
                    navigate(`/tasks/${item.task_id}`);
                  }
                }}
              >
                <div style={{ width: "100%" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <Text
                      strong
                      style={{
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                        lineHeight: 1.35,
                        flex: 1,
                      }}
                    >
                      {item.task_title || "Bez názvu"}
                    </Text>
                  </div>
                  <div>
                    <Text type="secondary">{item.inventory_name || "—"}</Text>
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <Text style={{ wordBreak: "break-word", lineHeight: 1.45 }}>
                      {item.question_text || "—"}
                      <Text type="secondary">: {getTaskAnswerDisplayText(item)}</Text>
                    </Text>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap",
                      marginTop: 4,
                    }}
                  >
                    <Tag style={{ margin: 0 }}>
                      {item.task_created_at
                        ? dayjs(item.task_created_at).format("DD.MM.YYYY HH:mm")
                        : "—"}
                    </Tag>
                    <Tag style={{ margin: 0, maxWidth: "100%" }}>
                      <span
                        style={{
                          display: "inline-block",
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          verticalAlign: "bottom",
                        }}
                      >
                        {item.initiator_name || item.initiator_login || "—"}
                      </span>
                    </Tag>
                  </div>
                </div>
              </List.Item>
            );
          }}
        />
      </div>

      {hasTaskIdInUrl ? null : (
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={total}
          showSizeChanger
          pageSizeOptions={["10", "20", "50"]}
          onChange={(page, size) => {
            setCurrentPage(page);
            if (size !== pageSize) {
              setPageSize(size);
              setCurrentPage(1);
            }
          }}
          style={{ marginTop: 12, textAlign: "right" }}
        />
      )}

      <Modal
        open={Boolean(selectedTask)}
        onCancel={closeTaskModal}
        title={selectedTask?.task_title || "Detail úkolu"}
        footer={[
          <Button key="close" onClick={closeTaskModal}>
            Zavřít
          </Button>,
          <Button
            key="resolve"
            type="primary"
            loading={updatingTask}
            disabled={!selectedTask?.task_id}
            onClick={async () => {
              if (!selectedTask?.task_id) return;
              setUpdatingTask(true);
              try {
                await updateTaskMutation({
                  variables: {
                    input: {
                      id: selectedTask.task_id,
                      assignee_id: userId,
                      status: "completed",
                    },
                  },
                });
                message.success("Úkol byl označen jako vyřešený.");
                closeTaskModal();
                await refetchTaskLists();
              } catch (e) {
                console.error("updateTask error:", e);
                message.error("Nepodařilo se aktualizovat úkol.");
              } finally {
                setUpdatingTask(false);
              }
            }}
          >
            Vyřešeno
          </Button>,
        ]}
        width="100vw"
        style={{ top: 0, paddingBottom: 0 }}
        styles={{ body: { maxHeight: "calc(100vh - 140px)", overflowY: "auto" } }}
      >
        {selectedTask ? (
          <Space direction="vertical" size={10} style={{ width: "100%" }}>
            <Tag color={getTaskStatusTagColor(selectedTask.task_status)}>
              {getTaskStatusLabel(selectedTask.task_status)}
            </Tag>
            <Paragraph>
              <Text strong>Event: </Text>
              {selectedTask.event_name || "—"}
            </Paragraph>
            <Paragraph>
              <Text strong>Místo: </Text>
              {selectedTask.place_name || "—"}
            </Paragraph>
            <Paragraph>
              <Text strong>Inventář: </Text>
              {selectedTask.inventory_name || "—"}
            </Paragraph>
            <Paragraph>
              <Text strong>Otázka: </Text>
              {selectedTask.question_text || "—"}
            </Paragraph>
            <Paragraph>
              <Text strong>Odpověď: </Text>
              {getTaskAnswerDisplayText(selectedTask)}
            </Paragraph>
            <Paragraph>
              <Text strong>Poznámka: </Text>
              {selectedTask.check_note || "—"}
            </Paragraph>
            <Paragraph>
              <Text strong>Iniciátor: </Text>
              {selectedTask.initiator_name || selectedTask.initiator_login || "—"}
            </Paragraph>
            <Paragraph>
              <Text strong>Assignee: </Text>
              {selectedTask.assignee_name || selectedTask.assignee_login || "—"}
            </Paragraph>
            <Paragraph>
              <Text strong>Vytvořeno: </Text>
              {selectedTask.task_created_at ? dayjs(selectedTask.task_created_at).format("DD.MM.YYYY HH:mm") : "—"}
            </Paragraph>
          </Space>
        ) : null}
      </Modal>
    </div>
  );
}
